﻿(() => {
  class OwnArtProductGallery {
    constructor(section) {
      this.section = section;
      this.track = section.querySelector('[data-gallery-track]');
      this.slides = Array.from(section.querySelectorAll('[data-gallery-slide]'));
      this.dots = Array.from(section.querySelectorAll('[data-gallery-dot]'));
      this.modal = section.querySelector('[data-gallery-modal]');
      this.modalItems = Array.from(section.querySelectorAll('[data-gallery-modal-item]'));
      this.currentIndex = 0;
      this.interval = Math.max(Number(section.dataset.galleryInterval || 1500), 1500);
      this.autoplay = section.dataset.galleryAutoplay !== 'false';
      this.continuousScroll = section.dataset.continuousScroll === 'true';
      this.productsUrl = section.dataset.galleryProductsUrl;
      this.productLimit = Number(section.dataset.galleryProductLimit || 10);
      this.timer = null;

      if (!this.track) return;

      this.bindEvents();
      if (this.slides.length > 0) this.setActive(0, false);
      this.hydrateProducts();
      if (!this.continuousScroll) this.start();
    }

    bindEvents() {
      this.section.querySelector('[data-gallery-prev]')?.addEventListener('click', () => {
        if (this.continuousScroll) return;
        this.previous();
        this.restart();
      });

      this.section.querySelector('[data-gallery-next]')?.addEventListener('click', () => {
        if (this.continuousScroll) return;
        this.next();
        this.restart();
      });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.setActive(Number(dot.dataset.galleryIndex || 0));
          this.restart();
        });
      });

      this.section.querySelectorAll('[data-gallery-open]').forEach((button) => {
        button.addEventListener('click', () => this.openModal(Number(button.dataset.galleryIndex || 0)));
      });

      this.section.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.closeModal());
      this.section.querySelector('[data-gallery-modal-prev]')?.addEventListener('click', () => this.modalPrevious());
      this.section.querySelector('[data-gallery-modal-next]')?.addEventListener('click', () => this.modalNext());

      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) this.closeModal();
      });

      this.section.addEventListener('mouseenter', () => this.stop());
      this.section.addEventListener('mouseleave', () => {
        if (!this.continuousScroll) this.start();
      });
      this.section.addEventListener('focusin', () => this.stop());
      this.section.addEventListener('focusout', () => {
        if (!this.continuousScroll) this.start();
      });

      document.addEventListener('keyup', (event) => {
        if (!this.modal || this.modal.hidden) return;
        if (event.code === 'Escape') this.closeModal();
        if (event.code === 'ArrowLeft') this.modalPrevious();
        if (event.code === 'ArrowRight') this.modalNext();
      });
    }

    async hydrateProducts() {
      if (!this.productsUrl) return;

      try {
        const response = await fetch(this.productsUrl, { credentials: 'same-origin' });
        if (!response.ok) return;

        const data = await response.json();
        const products = Array.isArray(data.products) ? data.products : [];
        const availableProducts = products
          .filter((product) => Array.isArray(product.variants) && product.variants.some((variant) => variant.available))
          .filter((product) => this.productImage(product))
          .slice(0, this.productLimit || 10);

        if (availableProducts.length === 0) {
          this.refreshElements();
          this.setActive(0, false);
          this.start();
          return;
        }

        this.renderProducts(availableProducts);
        this.refreshElements();
        this.setActive(0, false);
        this.restart();
      } catch (error) {
        console.warn('OwnArt product gallery could not load products.', error);
      }
    }

    clearProducts() {
      this.stop();
      this.track.innerHTML = '';

      const controls = this.section.querySelector('.ownart-product-gallery__controls');
      if (controls) controls.hidden = true;

      const modalStage = this.section.querySelector('[data-gallery-modal-stage]');
      if (modalStage) modalStage.innerHTML = '';

      this.refreshElements();
    }

    renderProducts(products) {
      this.track.innerHTML = products.map((product, index) => this.productSlideMarkup(product, index)).join('');

      const controls = this.section.querySelector('.ownart-product-gallery__controls');
      if (controls) {
        controls.hidden = products.length < 2;
        const dots = controls.querySelector('[data-gallery-dots]');
        if (dots) {
          dots.innerHTML = products.map((product, index) => `
            <button
              type="button"
              class="ownart-product-gallery__dot${index === 0 ? ' is-active' : ''}"
              data-gallery-dot
              data-gallery-index="${index}"
              aria-label="Show product image ${index + 1}"
              aria-pressed="${index === 0 ? 'true' : 'false'}"
            ></button>
          `).join('');
        }
      }

      const modalStage = this.section.querySelector('[data-gallery-modal-stage]');
      if (modalStage) {
        modalStage.innerHTML = products.map((product, index) => this.productModalMarkup(product, index)).join('');
      }
    }

    productSlideMarkup(product, index) {
      const image = this.productImage(product);
      const price = this.productPrice(product);
      const vendor = this.escapeHtml(product.vendor || '');
      const title = this.escapeHtml(product.title || '');
      const body = this.escapeHtml(this.stripHtml(product.body_html || product.body || '')).slice(0, 92);
      const url = `/products/${this.escapeAttribute(product.handle || '')}`;

      return `
        <li class="multicolumn-list__item grid__item" data-gallery-slide data-gallery-index="${index}">
          <div class="multicolumn-card content-container">
            <div class="multicolumn-card__image-wrapper multicolumn-card__image-wrapper--full-width multicolumn-card-spacing">
              <div class="media media--transparent media--adapt ownart-product-gallery__media">
                <button type="button" class="ownart-product-gallery__media-button" data-gallery-open data-gallery-index="${index}" aria-label="Open ${title} image">
                  ${image ? `<img src="${this.escapeAttribute(image)}" alt="${title}" class="multicolumn-card__image" loading="lazy">` : ''}
                </button>
              </div>
            </div>
            <div class="multicolumn-card__info">
              ${vendor ? `<p class="ownart-product-gallery__vendor">${vendor}</p>` : ''}
              <h3 class="inline-richtext">${title}</h3>
              ${body ? `<div class="rte">${body}</div>` : ''}
              ${price ? `<p class="ownart-product-gallery__price">${price}</p>` : ''}
              <a class="link animate-arrow" href="${url}">View product<span class="svg-wrapper"><span class="icon-wrap">&nbsp;&rarr;</span></span></a>
            </div>
          </div>
        </li>
      `;
    }

    productModalMarkup(product, index) {
      const image = this.productImage(product);
      const title = this.escapeHtml(product.title || '');

      return `
        <figure class="ownart-product-gallery__modal-item${index === 0 ? ' is-active' : ''}" data-gallery-modal-item data-gallery-index="${index}">
          ${image ? `<img src="${this.escapeAttribute(image)}" alt="${title}" class="ownart-product-gallery__modal-image">` : ''}
          <figcaption class="ownart-product-gallery__modal-caption">${title}</figcaption>
        </figure>
      `;
    }

    refreshElements() {
      this.slides = Array.from(this.section.querySelectorAll('[data-gallery-slide]'));
      this.dots = Array.from(this.section.querySelectorAll('[data-gallery-dot]'));
      this.modalItems = Array.from(this.section.querySelectorAll('[data-gallery-modal-item]'));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.setActive(Number(dot.dataset.galleryIndex || 0));
          this.restart();
        });
      });

      this.section.querySelectorAll('[data-gallery-open]').forEach((button) => {
        button.addEventListener('click', () => this.openModal(Number(button.dataset.galleryIndex || 0)));
      });
    }

    productImage(product) {
      if (product.image?.src) return product.image.src;
      if (Array.isArray(product.images) && product.images[0]?.src) return product.images[0].src;
      return '';
    }

    productPrice(product) {
      const variant = Array.isArray(product.variants) ? product.variants.find((item) => item.available) || product.variants[0] : null;
      if (!variant?.price) return '';
      return `$${Number(variant.price).toFixed(2)}`;
    }

    galleryImageRatio() {
      if (this.track.className.includes('media--square')) return 'square';
      return 'adapt';
    }

    stripHtml(html) {
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }

    setActive(index, shouldScroll = true) {
      this.currentIndex = this.normalizeIndex(index);
      const currentSlide = this.slides[this.currentIndex];

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.currentIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', 'false');
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (shouldScroll && currentSlide) {
        if (this.continuousScroll) return;
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        if (maxScroll <= 2) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
          return;
        }

        this.track.scrollTo({
          left: Math.min(currentSlide.offsetLeft, maxScroll),
          behavior: 'smooth',
        });
      }
    }

    previous() {
      this.setActive(this.currentIndex - 1);
    }

    next() {
      this.setActive(this.currentIndex + 1);
    }

    openModal(index) {
      if (!this.modal) return;
      this.stop();
      this.setActive(index);
      this.setModalActive(this.currentIndex);
      this.modal.hidden = false;
      document.body.classList.add('ownart-gallery-modal-open');
      this.section.querySelector('[data-gallery-close]')?.focus();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.classList.remove('ownart-gallery-modal-open');
      this.section.querySelector(`[data-gallery-open][data-gallery-index="${this.currentIndex}"]`)?.focus();
      if (!this.continuousScroll) this.start();
    }

    modalPrevious() {
      this.setActive(this.currentIndex - 1);
      this.setModalActive(this.currentIndex);
    }

    modalNext() {
      this.setActive(this.currentIndex + 1);
      this.setModalActive(this.currentIndex);
    }

    setModalActive(index) {
      this.modalItems.forEach((item) => {
        const isActive = Number(item.dataset.galleryIndex || 0) === index;
        item.classList.toggle('is-active', isActive);
        item.hidden = !isActive;
      });
    }

    normalizeIndex(index) {
      if (this.slides.length === 0) return 0;
      if (index < 0) return this.slides.length - 1;
      if (index >= this.slides.length) return 0;
      return index;
    }

    start() {
      if (!this.autoplay || this.slides.length < 2 || this.modal?.hidden === false) return;
      this.stop();
      this.timer = window.setInterval(() => this.next(), this.interval);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  const initGalleries = (container = document) => {
    container.querySelectorAll('[data-ownart-product-gallery]').forEach((section) => {
      if (section.dataset.galleryInitialized === 'true') return;
      section.dataset.galleryInitialized = 'true';
      new OwnArtProductGallery(section);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initGalleries());
  document.addEventListener('shopify:section:load', (event) => initGalleries(event.target));
})();
